import { inject, Injectable } from '@angular/core';
import { DocumentService } from '@app/features/documents/document-service';
import { CdTDocument, Orientation, PageOrientations } from '@app/features/documents/document.model';
import { catchError, concatMap, map, Observable, of, Subject } from 'rxjs';

/** Une rotation à enregistrer. */
export interface OrientationSave {
  document: CdTDocument;
  page: number;
  degrees: Orientation;
}

/** Une rotation enregistrée, ou qui n'a pas pu l'être. */
export interface OrientationSaveResult extends OrientationSave {
  saved: boolean;
}

/**
 * File des orientations à enregistrer (RG-document-orientation-memorisee). Elle vit aussi
 * longtemps que l'application et non que la visionneuse : fermer celle-ci n'annule ni
 * l'enregistrement en cours, ni les gestes qui l'attendent.
 *
 * Un échec est signalé par la gestion d'erreurs globale (`HttpErrorInterceptor`), que la
 * visionneuse soit encore ouverte ou non ; ouverte, elle avertit en plus que la page ne restera
 * pas orientée.
 */
@Injectable({ providedIn: 'root' })
export class DocumentOrientationService {
  private readonly documentService = inject(DocumentService);
  private readonly queue = new Subject<OrientationSave>();
  private readonly resultsSubject = new Subject<OrientationSaveResult>();
  /** Gestes envoyés ou en attente, dans leur ordre. */
  private readonly pending: OrientationSave[] = [];

  constructor() {
    // Une rotation après l'autre : l'API retient ainsi le dernier geste, et non la réponse la
    // plus lente.
    this.queue
      .pipe(
        concatMap((save) =>
          this.documentService.setOrientation(save.document.id, save.page, save.degrees).pipe(
            map(() => ({ save, saved: true })),
            catchError(() => of({ save, saved: false })),
          ),
        ),
      )
      .subscribe(({ save, saved }) => this.onSaved(save, saved));
  }

  /** Résultat de chaque enregistrement, dans l'ordre des gestes. */
  get results$(): Observable<OrientationSaveResult> {
    return this.resultsSubject.asObservable();
  }

  /** Met une rotation dans la file ; elle part dès que les précédentes ont abouti. */
  save(document: CdTDocument, page: number, degrees: Orientation): void {
    const save = { document, page, degrees };
    this.pending.push(save);
    this.queue.next(save);
  }

  /**
   * Orientations d'un document qui attendent encore leur enregistrement, le dernier geste de
   * chaque page l'emportant : rouvert entre-temps, le document s'affiche tel qu'on l'a laissé.
   */
  pendingOrientations(document: CdTDocument): PageOrientations {
    return Object.fromEntries(
      this.pending
        .filter((save) => save.document.id === document.id)
        .map((save) => [String(save.page), save.degrees]),
    );
  }

  private onSaved(save: OrientationSave, saved: boolean): void {
    this.pending.splice(this.pending.indexOf(save), 1);
    const { document, page, degrees } = save;
    if (saved) {
      // Le document est partagé avec le contrat qui l'a ouvert : la consultation suivante
      // l'affichera ainsi orienté sans le recharger.
      document.orientations = { ...(document.orientations ?? {}), [String(page)]: degrees };
    }
    this.resultsSubject.next({ ...save, saved });
  }
}
